// src/components/Transactions/EditCategorized.js - Atualizado para suportar colaboradores
import React, { useState, useEffect } from "react";
import { auth, db, storage } from "../../firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp
} from "firebase/firestore";
import { ref, getBlob } from "firebase/storage";
import { parseOFXContent } from "../../utils/OFXParser";
import MainLayout from "../Layout/MainLayout";
import "./Transactions.css";
import { cleanTransactionName } from "../../utils/cleanTransactionName";
import { useUserContext } from "../../contexts/UserContext";

// Importar serviços refatorados para garantir consistência
import { 
  loadAvailablePeriods,
  loadCategoryMappings,
  loadTransactionsForPeriod
} from "../../services/transactionRepository";

import { checkTransactionCategorization } from "../../services/categorizationService";

const EditCategorized = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [categories, setCategories] = useState({});
  const [flatCategories, setFlatCategories] = useState([]);
  const [categoryMappings, setCategoryMappings] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [editScope, setEditScope] = useState("single"); // "single" ou "all"
  
  // Obter o contexto completo do usuário incluindo perfis ativos
  const { 
    activeAccountType, 
    getAccountSubpath,
    activeBusinessProfileId,
    businessProfiles,
    isCollaborator 
  } = useUserContext();

  // Estado para guardar ID do proprietário do workspace (para colaboradores)
  const [workspaceOwner, setWorkspaceOwner] = useState(null);
  
  const transactionsPerPage = 10;

  // Obter informações do perfil ativo para exibição
  const getActiveProfileInfo = () => {
    if (activeAccountType === 'personal') {
      return { name: 'Pessoal', type: 'personal' };
    } else {
      const activeProfile = businessProfiles.find(p => p.id === activeBusinessProfileId);
      return { 
        name: activeProfile?.profileName || 'Empresarial', 
        type: 'business',
        profileId: activeBusinessProfileId 
      };
    }
  };

  const activeProfileInfo = getActiveProfileInfo();

  // Carregar usuário, períodos e categorias
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        if (auth.currentUser) {
          setUser(auth.currentUser);
          
          // Verificar se o usuário é um colaborador
          const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
          if (userDoc.exists() && userDoc.data().isCollaborator === true) {
            // Verificar informações do workspace
            const invitedBy = userDoc.data().invitedBy;
            const originalInvitation = userDoc.data().originalInvitation || {};
            const profileId = originalInvitation.profileId;
            
            // Verificar se está acessando o workspace para o qual foi convidado
            if (profileId && invitedBy && activeBusinessProfileId === profileId) {
              setWorkspaceOwner(invitedBy);
              console.log(`Colaborador detectado. Proprietário do workspace: ${invitedBy}`);
            }
          }

          // Usar o serviço refatorado para carregar períodos
          const availablePeriods = await loadAvailablePeriods();
          setPeriods(availablePeriods);

          // Verificar se existe um período salvo no localStorage
          let savedPeriod = localStorage.getItem('selectedPeriod');
          let periodToUse;

          if (savedPeriod && availablePeriods.find(p => p.value === savedPeriod)) {
            periodToUse = savedPeriod;
          } else if (availablePeriods.length > 0) {
            periodToUse = availablePeriods[0].value;
            localStorage.setItem('selectedPeriod', periodToUse);
          }

          if (periodToUse) {
            setSelectedPeriod(periodToUse);
          }

          // Carregar categorias do usuário usando a lógica correta de perfis
          await loadUserCategories();

          // Usar o serviço refatorado para carregar mapeamentos
          const mappings = await loadCategoryMappings();
          setCategoryMappings(mappings);
        }
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
        setError("Não foi possível carregar os dados iniciais.");
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [activeAccountType, activeBusinessProfileId]); // Incluir activeBusinessProfileId como dependência

  // Carregar categorias do usuário com lógica correta de perfis
  const loadUserCategories = async () => {
    try {
      if (!auth.currentUser) return;

      const accountSubpath = getAccountSubpath();
      const isPersonal = activeAccountType === 'personal';
      
      // Verificar se o usuário é um colaborador e obter o ID do proprietário
      let ownerUserId = auth.currentUser.uid;
      
      // Verificar no documento do usuário se é um colaborador
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDoc.exists() && userDoc.data().isCollaborator === true) {
        // Verificar informações do workspace
        const invitedBy = userDoc.data().invitedBy;
        const originalInvitation = userDoc.data().originalInvitation || {};
        const profileId = originalInvitation.profileId;
        
        // Verificar se o perfil ativo corresponde ao perfil para o qual foi convidado
        if (activeBusinessProfileId === profileId && invitedBy) {
          ownerUserId = invitedBy; // Usar o ID do proprietário
          console.log(`Colaborador detectado. Carregando categorias do proprietário: ${ownerUserId}`);
        }
      }
      
      let userCategoriesDocRef;
      
      if (isPersonal) {
        // Para conta pessoal, usar a subcoleção "personal/categories"
        userCategoriesDocRef = doc(db, "userCategories", ownerUserId, "personal", "categories");
      } else {
        // Para perfil empresarial, usar a lógica baseada no perfil ativo
        if (accountSubpath === 'business') {
          // Para o perfil principal, verificar primeiro no novo caminho estruturado
          userCategoriesDocRef = doc(db, "userCategories", ownerUserId, "business", "categories");
          
          // Verificar se o documento existe
          const docSnapshot = await getDoc(userCategoriesDocRef);
          
          // Se não existir, tentar o caminho legado (retrocompatibilidade)
          if (!docSnapshot.exists()) {
            console.log(`Documento de categorias não encontrado para perfil business, tentando caminho antigo...`);
            userCategoriesDocRef = doc(db, "userCategories", ownerUserId);
          }
        } else {
          // Para outros perfis empresariais, usar o ID do perfil
          userCategoriesDocRef = doc(db, "userCategories", ownerUserId, accountSubpath, "categories");
        }
      }
        
      const userCategoriesDoc = await getDoc(userCategoriesDocRef);
      
      if (userCategoriesDoc.exists()) {
        const categoriesData = userCategoriesDoc.data();
        
        // Process categories into a more usable format
        if (categoriesData.categories) {
          // Extrair categorias selecionadas organizadas por grupo
          const categoriesByGroup = {};
          const flattenedCategories = [];
          
          Object.keys(categoriesData.categories).forEach(path => {
            if (categoriesData.categories[path]) {
              const parts = path.split('.');
              if (parts.length === 2) {
                const group = parts[0];
                const item = parts[1];
                
                if (!categoriesByGroup[group]) {
                  categoriesByGroup[group] = {
                    items: []
                  };
                }
                
                categoriesByGroup[group].items.push(item);
                
                // Adicionar ao array de categorias planas
                flattenedCategories.push({
                  label: item,
                  value: path,
                  group: group
                });
              }
            }
          });
          
          // Ordenar alfabeticamente
          flattenedCategories.sort((a, b) => a.label.localeCompare(b.label));
          
          setCategories(categoriesByGroup);
          setFlatCategories(flattenedCategories);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      setError("Não foi possível carregar suas categorias.");
    }
  };

  // Efeito para carregar transações quando mudar período
  useEffect(() => {
    if (selectedPeriod) {
      loadCategorizedTransactions();
    }
  }, [selectedPeriod, categoryMappings, activeAccountType, activeBusinessProfileId]);

  // Efeito para filtrar transações quando mudar a busca ou filtro
  useEffect(() => {
    if (transactions.length > 0) {
      filterTransactions();
    }
  }, [transactions, searchTerm, filterCategory]);

  // Função para filtrar transações
  const filterTransactions = () => {
    let filtered = [...transactions];

    // Aplicar filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(transaction =>
        transaction.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Aplicar filtro de categoria
    if (filterCategory) {
      filtered = filtered.filter(transaction =>
        transaction.categoryPath && transaction.categoryPath.startsWith(filterCategory)
      );
    }

    setFilteredTransactions(filtered);

    // Resetar página atual e calcular total de páginas
    setCurrentPage(1);
    setTotalPages(Math.max(1, Math.ceil(filtered.length / transactionsPerPage)));
  };

  // Carregar transações categorizadas usando lógica correta de perfis
  const loadCategorizedTransactions = async () => {
    try {
      setLoading(true);

      if (!auth.currentUser || !selectedPeriod) return;

      // Usar o serviço refatorado para obter transações (já lida com colaboradores)
      const { transactions: allTransactions } = await loadTransactionsForPeriod(selectedPeriod);
      
      const categorizedTransactions = [];

      // Identificar mapeamentos específicos por ID de transação
      const specificMappings = {};
      Object.entries(categoryMappings).forEach(([key, mapping]) => {
        if (mapping.isSpecificMapping && mapping.transactionId) {
          specificMappings[mapping.transactionId] = mapping;
        }
      });

      // Processar cada transação usando o serviço de categorização
      allTransactions.forEach(transaction => {
        const categorizationResult = checkTransactionCategorization(transaction, categoryMappings);
        
        if (categorizationResult.isCategorized) {
          categorizedTransactions.push({
            ...transaction,
            fileId: transaction.fileId || 'unknown',
            fileName: transaction.fileName || 'unknown',
            period: transaction.period || selectedPeriod,
            periodLabel: transaction.periodLabel || selectedPeriod,
            category: categorizationResult.subCategory,
            categoryPath: categorizationResult.categoryPath,
            groupName: categorizationResult.mainCategory,
            isSpecificMapping: categorizationResult.source === 'specific_mapping',
            autoMapped: categorizationResult.autoMapped || false,
            categorizationSource: categorizationResult.source
          });
        }
      });

      // Ordenar transações
      categorizedTransactions.sort((a, b) => {
        if (a.categoryPath && b.categoryPath && a.categoryPath !== b.categoryPath) {
          return a.categoryPath.localeCompare(b.categoryPath);
        }
        return new Date(b.date) - new Date(a.date);
      });

      setTransactions(categorizedTransactions);
      setFilteredTransactions(categorizedTransactions);
      setTotalPages(Math.max(1, Math.ceil(categorizedTransactions.length / transactionsPerPage)));
      setCurrentPage(1);
    } catch (error) {
      console.error("Erro ao carregar transações categorizadas:", error);
      setError("Não foi possível carregar as transações categorizadas.");
    } finally {
      setLoading(false);
    }
  };

  // Função para alterar o período
  const handlePeriodChange = (event) => {
    const period = event.target.value;
    setSelectedPeriod(period);
    localStorage.setItem('selectedPeriod', period);
  };

  // Obter transações para a página atual
  const getCurrentTransactions = () => {
    const startIndex = (currentPage - 1) * transactionsPerPage;
    const endIndex = startIndex + transactionsPerPage;
    return filteredTransactions.slice(startIndex, endIndex);
  };

  // Função para mudar a página
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Formatar data
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR').format(date);
  };

  // Formatar valor monetário
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Abrir modal de edição
  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setNewCategory(transaction.categoryPath);
    setEditScope("single");
  };

  // Fechar modal de edição
  const handleCloseEdit = () => {
    setEditingTransaction(null);
    setNewCategory("");
    setEditScope("single");
  };

  // Salvar edição de categoria - Atualizado para colaboradores
  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      setError("");

      if (!editingTransaction || !newCategory) {
        setError("Selecione uma categoria válida.");
        setLoading(false);
        return;
      }

      const selectedCategoryObj = flatCategories.find(cat => cat.value === newCategory);
      if (!selectedCategoryObj) {
        setError("Categoria selecionada inválida.");
        setLoading(false);
        return;
      }

      // Verificar se a nova categoria é de Receita para uma transação negativa (ou vice-versa)
      const transactionAmount = editingTransaction.amount;
      const newCategoryIsReceita = newCategory.toUpperCase().startsWith("RECEITA.");
      const newCategoryIsDespesa = newCategory.toUpperCase().includes("DESPESA") || newCategory.startsWith("(-)");

      if ((newCategoryIsReceita && transactionAmount < 0) || (newCategoryIsDespesa && transactionAmount > 0)) {
          setError("Não é possível categorizar uma despesa como receita ou uma receita como despesa. Verifique a categoria selecionada.");
          setLoading(false);
          return;
      }

      const normalizedDescription = editingTransaction.description.trim().toLowerCase();
      const bankName = editingTransaction.bankInfo?.org || "";
      const updatedMappings = { ...categoryMappings };
      let mappingKeyToUpdate;

      // Determinar a chave de mapeamento correta
      const isSicoob =
        bankName.toUpperCase().includes("SICOOB") ||
        bankName.toUpperCase().includes("COOP DE CRED") ||
        editingTransaction.description.toUpperCase().includes("SICOOB") ||
        editingTransaction.description.toUpperCase().includes("SICREDI");

      if (isSicoob && editScope === "all" && !editingTransaction.isSpecificMapping) {
          const sicoobNormalizedDesc = cleanTransactionName(editingTransaction.description, bankName).toLowerCase();
          mappingKeyToUpdate = `sicoob_${sicoobNormalizedDesc}`;
      } else if (editScope === "all" && !editingTransaction.isSpecificMapping) {
          mappingKeyToUpdate = normalizedDescription;
      } else {
          mappingKeyToUpdate = `specific_${editingTransaction.id}_${Date.now()}`;
      }

      if (editScope === "all") {
        // Atualizar mapeamento para todas as transações com a mesma descrição (ou padrão SICOOB)
        updatedMappings[mappingKeyToUpdate] = {
          categoryName: selectedCategoryObj.label,
          categoryPath: newCategory,
          groupName: selectedCategoryObj.group,
          lastUsed: new Date(),
          isSpecificMapping: false,
          ...(isSicoob && { isSicoobPattern: true, normalizedDescription: cleanTransactionName(editingTransaction.description, bankName).toLowerCase() }),
          positiveOnly: newCategory.toUpperCase().startsWith("RECEITA."),
          negativeOnly: newCategory.toUpperCase().includes("DESPESA") || newCategory.startsWith("(-)"),
          bankInfo: editingTransaction.bankInfo ? { org: editingTransaction.bankInfo.org } : null,
          sampleDescription: editingTransaction.description
        };
      } else {
        // Criar/atualizar um mapeamento específico para esta transação
        updatedMappings[mappingKeyToUpdate] = {
          categoryName: selectedCategoryObj.label,
          categoryPath: newCategory,
          groupName: selectedCategoryObj.group,
          lastUsed: new Date(),
          originalDescription: normalizedDescription,
          isSpecificMapping: true,
          transactionId: editingTransaction.id,
          positiveOnly: newCategory.toUpperCase().startsWith("RECEITA."),
          negativeOnly: newCategory.toUpperCase().includes("DESPESA") || newCategory.startsWith("(-)"),
          bankInfo: editingTransaction.bankInfo ? { org: editingTransaction.bankInfo.org } : null,
          sampleDescription: editingTransaction.description
        };
      }

      // MODIFICADO: Verificar se o usuário é um colaborador e obter o ID do proprietário
      let ownerUserId = auth.currentUser.uid;
      
      // Verificar no documento do usuário se é um colaborador (caso não tenha sido verificado antes)
      if (!workspaceOwner) {
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userDoc.exists() && userDoc.data().isCollaborator === true) {
          // Verificar informações do workspace
          const invitedBy = userDoc.data().invitedBy;
          const originalInvitation = userDoc.data().originalInvitation || {};
          const profileId = originalInvitation.profileId;
          
          // Verificar se o perfil ativo corresponde ao perfil para o qual foi convidado
          if (activeBusinessProfileId === profileId && invitedBy) {
            ownerUserId = invitedBy; // Usar o ID do proprietário
            setWorkspaceOwner(invitedBy);
            console.log(`Colaborador detectado. Usando ID do proprietário: ${ownerUserId}`);
          }
        }
      } else {
        ownerUserId = workspaceOwner;
      }
      
      const accountSubpath = getAccountSubpath();
      const isPersonal = activeAccountType === 'personal';
      
      let categoryMappingsRef;
      
      if (isPersonal) {
        // Para conta pessoal, usar a subcoleção "personal/mappings"
        categoryMappingsRef = doc(db, "categoryMappings", ownerUserId, "personal", "mappings");
      } else {
        // Para perfil empresarial, usar a lógica baseada no perfil ativo
        if (accountSubpath === 'business') {
          // Para o perfil principal, usar o caminho estruturado
          categoryMappingsRef = doc(db, "categoryMappings", ownerUserId, "business", "mappings");
          
          // Verificar se o documento existe
          const docSnapshot = await getDoc(categoryMappingsRef);
          
          // Se não existir, também atualizar o caminho legado
          if (!docSnapshot.exists()) {
            const legacyMappingsRef = doc(db, "categoryMappings", ownerUserId);
            await setDoc(legacyMappingsRef, {
              mappings: updatedMappings,
              updatedAt: serverTimestamp(),
              // Adicionar info do colaborador se aplicável
              ...(isCollaborator ? {
                lastEditedBy: auth.currentUser.uid,
                lastEditedByName: auth.currentUser.displayName || "Colaborador",
                lastEditedAt: serverTimestamp()
              } : {})
            });
          }
        } else {
          // Para outros perfis empresariais, usar o ID do perfil
          categoryMappingsRef = doc(db, "categoryMappings", ownerUserId, accountSubpath, "mappings");
        }
      }

      // Verificar se o documento já existe
      const docSnapshot = await getDoc(categoryMappingsRef);
      
      if (docSnapshot.exists()) {
        // Atualizar o documento existente
        if (isPersonal) {
          // Para conta pessoal, pode não usar a chave 'mappings'
          await updateDoc(categoryMappingsRef, {
            ...updatedMappings,
            // Adicionar info do colaborador se aplicável
            ...(isCollaborator ? {
              lastEditedBy: auth.currentUser.uid,
              lastEditedByName: auth.currentUser.displayName || "Colaborador",
              lastEditedAt: serverTimestamp()
            } : {})
          });
        } else {
          // Para conta empresarial, usar a estrutura padrão
          await updateDoc(categoryMappingsRef, {
            mappings: updatedMappings,
            updatedAt: serverTimestamp(),
            // Adicionar info do colaborador se aplicável
            ...(isCollaborator ? {
              lastEditedBy: auth.currentUser.uid,
              lastEditedByName: auth.currentUser.displayName || "Colaborador",
              lastEditedAt: serverTimestamp()
            } : {})
          });
        }
      } else {
        // Criar um novo documento
        if (isPersonal) {
          await setDoc(categoryMappingsRef, {
            ...updatedMappings,
            // Adicionar info do colaborador se aplicável
            ...(isCollaborator ? {
              createdBy: auth.currentUser.uid,
              createdByName: auth.currentUser.displayName || "Colaborador",
              createdAt: serverTimestamp()
            } : {})
          });
        } else {
          await setDoc(categoryMappingsRef, {
            mappings: updatedMappings,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            // Adicionar info do colaborador se aplicável
            ...(isCollaborator ? {
              createdBy: auth.currentUser.uid,
              createdByName: auth.currentUser.displayName || "Colaborador"
            } : {})
          });
        }
      }

      setCategoryMappings(updatedMappings);

      if (editScope === "all") {
        setSuccess(`Todas as transações com a descrição "${editingTransaction.description}" (ou padrão correspondente) foram atualizadas para "${selectedCategoryObj.label}".`);
      } else {
        setSuccess(`A transação "${editingTransaction.description}" foi atualizada para "${selectedCategoryObj.label}".`);
      }

      handleCloseEdit();
      await loadCategorizedTransactions(); // Recarregar para refletir mudanças

      setTimeout(() => setSuccess(""), 3000);

    } catch (error) {
      console.error("Erro ao atualizar categoria:", error);
      setError("Não foi possível atualizar a categoria. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Obter categorias únicas para o filtro
  const uniqueCategories = [...new Set(
    transactions
      .filter(t => t.groupName)
      .map(t => t.groupName)
  )];

  if (loading && (!selectedPeriod || Object.keys(categories).length === 0)) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando dados...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout userName={user?.displayName || "Usuário"}>
      <div className="edit-categorized-container">
        {/* Seletor de período com informações do perfil ativo */}
        <div className="period-selector-container">
          <div className="period-selector-header">
            <h2>
              Editar Itens Categorizados - {activeProfileInfo.name}
              {activeProfileInfo.type === 'business' && activeProfileInfo.profileId && 
               activeProfileInfo.profileId !== 'business' && (
                <span className="profile-indicator"> (Perfil: {activeProfileInfo.profileId})</span>
              )}
              {isCollaborator && (
                <span className="collaborator-indicator"> (Modo Colaborador)</span>
              )}
            </h2>
            <div className="period-dropdown">
              <label htmlFor="period-select">Período: </label>
              <select
                id="period-select"
                value={selectedPeriod}
                onChange={handlePeriodChange}
                disabled={loading}
              >
                {periods.length === 0 ? (
                  <option value="">Nenhum período disponível</option>
                ) : (
                  periods.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        <div className="transactions-filters">
          <div className="search-filter">
            <input
              type="text"
              placeholder="Buscar por descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="filter-input"
            />

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="filter-select"
            >
              <option value="">Todas as categorias</option>
              {uniqueCategories.map((category, index) => (
                <option key={index} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="transactions-count">
            {filteredTransactions.length} transações categorizadas encontradas
          </div>
        </div>

        {loading ? (
          <div className="loading-data">
            <div className="loading-spinner"></div>
            <p>Carregando transações...</p>
          </div>
        ) : (
          <>
            {filteredTransactions.length > 0 ? (
              <div className="transactions-content">
                <div className="transactions-table-container">
                  <table className="transactions-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Descrição</th>
                        <th>Categoria</th>
                        <th>Valor</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getCurrentTransactions().map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{formatDate(transaction.date)}</td>
                          <td className="description-cell">
                            {transaction.description}
                          </td>
                          <td>
                            <span className="category-badge">
                              {transaction.category}
                              {transaction.autoMapped && <span title="Categorizada automaticamente"> (Auto)</span>}
                            </span>
                          </td>
                          <td className={transaction.amount >= 0 ? "amount-positive" : "amount-negative"}>
                            {formatCurrency(transaction.amount)}
                          </td>
                          <td>
                          <button
                          className="edit-category-button"
                          onClick={() => handleEditTransaction(transaction)}
                          aria-label="Editar categoria desta transação"
                        >
                          Editar Categoria
                        </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className="pagination-button"
                    >
                      &laquo;
                    </button>

                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="pagination-button"
                    >
                      &lt;
                    </button>

                    <span className="pagination-info">
                      Página {currentPage} de {totalPages}
                    </span>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="pagination-button"
                    >
                      &gt;
                    </button>

                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className="pagination-button"
                    >
                      &raquo;
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-transactions">
                <p>Não existem transações categorizadas para este período ou filtragem.</p>
              </div>
            )}
          </>
        )}

        {/* Modal de edição com estrutura aprimorada */}
        {editingTransaction && (
          <div className="edit-modal-overlay">
            <div className="edit-modal">
              <div className="edit-modal-header">
                <h3>Editar Categorização</h3>
                <button className="close-modal-button" onClick={handleCloseEdit}>×</button>
              </div>

              <div className="edit-modal-content">
                <div className="transaction-details">
                  <p>
                    <strong>Descrição:</strong>
                    {editingTransaction.description}
                  </p>
                  <p>
                    <strong>Data:</strong>
                    {formatDate(editingTransaction.date)}
                  </p>
                  <p>
                    <strong>Valor:</strong>
                    <span className={editingTransaction.amount >= 0 ? "amount-positive" : "amount-negative"}>
                      {formatCurrency(editingTransaction.amount)}
                    </span>
                  </p>
                  <p>
                    <strong>Categoria Atual:</strong>
                    <span className="category-badge">{editingTransaction.category}</span>
                  </p>
                </div>

                <div className="category-selection">
                  <label htmlFor="new-category-select">Nova Categoria:</label>
                  <select
                    id="new-category-select"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="category-select"
                  >
                    <option value="">Selecione uma categoria</option>
                    {flatCategories.map((category, index) => (
                      <option key={index} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="edit-scope-selection">
                  <p>Aplicar alteração:</p>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="editScope"
                        value="single"
                        checked={editScope === "single"}
                        onChange={() => setEditScope("single")}
                      />
                      Apenas para esta transação
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="editScope"
                        value="all"
                        checked={editScope === "all"}
                        onChange={() => setEditScope("all")}
                      />
                      Para todas as transações com este nome
                    </label>
                  </div>
                   {editScope === "all" && editingTransaction?.isSpecificMapping && (
                      <p className="warning-message">
                        Atenção: Esta transação foi categorizada individualmente. Aplicar "Para todas"
                        irá criar um novo mapeamento geral pela descrição, mas não alterará
                        outras transações que também foram categorizadas individualmente.
                      </p>
                    )}
                </div>
              </div>

              <div className="edit-modal-footer">
                <button className="cancel-button" onClick={handleCloseEdit}>
                  Cancelar
                </button>
                <button
                  className="save-button"
                  onClick={handleSaveEdit}
                  disabled={!newCategory || loading}
                >
                  {loading ? "Salvando..." : "Salvar Alterações"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Adicionar estilo CSS para indicador de colaborador */}
      <style>{`
        .collaborator-indicator {
          color: #0066cc;
          font-size: 0.8em;
          font-weight: normal;
          margin-left: 8px;
        }
      `}</style>
    </MainLayout>
  );
};

export default EditCategorized;